// RND-1: four stacked canvases, per-layer dirty flags, single rAF loop that idles when clean.
export type RenderLayerId = "bg" | "static" | "dynamic" | "overlay";

export const RENDER_LAYERS: RenderLayerId[] = ["bg", "static", "dynamic", "overlay"];

export type LayerRenderer = (ctx: CanvasRenderingContext2D, cssW: number, cssH: number, dpr: number) => void;

export class RenderScheduler {
  private canvases = new Map<RenderLayerId, HTMLCanvasElement>();
  private renderers = new Map<RenderLayerId, LayerRenderer>();
  private dirty = new Set<RenderLayerId>();
  private rafId: number | null = null;
  private cssW = 0;
  private cssH = 0;
  private dpr = 1;

  attach(id: RenderLayerId, canvas: HTMLCanvasElement, renderer: LayerRenderer): void {
    this.canvases.set(id, canvas);
    this.renderers.set(id, renderer);
  }

  get width(): number {
    return this.cssW;
  }

  get height(): number {
    return this.cssH;
  }

  get devicePixelRatio(): number {
    return this.dpr;
  }

  /** EC-ARC-5/6: resize all canvas bitmaps to CSS size × DPR; skip at 0×0. */
  resize(cssW: number, cssH: number, dpr: number): void {
    this.cssW = cssW;
    this.cssH = cssH;
    this.dpr = dpr;
    if (cssW <= 0 || cssH <= 0) return;
    for (const canvas of this.canvases.values()) {
      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);
    }
    this.invalidateAll();
  }

  invalidate(...ids: RenderLayerId[]): void {
    for (const id of ids) this.dirty.add(id);
    this.requestFrame();
  }

  invalidateAll(): void {
    this.invalidate(...RENDER_LAYERS);
  }

  private requestFrame(): void {
    // EC-RND-7: never more than one pending frame.
    if (this.rafId !== null) return;
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.renderDirty();
    });
  }

  private renderDirty(): void {
    if (this.cssW <= 0 || this.cssH <= 0) {
      this.dirty.clear();
      return;
    }
    for (const id of RENDER_LAYERS) {
      if (!this.dirty.has(id)) continue;
      this.dirty.delete(id);
      const canvas = this.canvases.get(id);
      const renderer = this.renderers.get(id);
      if (!canvas || !renderer) continue;
      const ctx = canvas.getContext("2d");
      if (!ctx) continue;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      renderer(ctx, this.cssW, this.cssH, this.dpr);
    }
  }
}
